import { useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { TaxCertificateData } from 'src/redux/slices/purchase-order/types';
import { splitSupplierTin } from 'src/utils/tax-certification/tin-utils';
import { decodeBase64Img } from 'src/utils';
import { eSign } from 'src/constants';
import { usePermissions } from '../use-permissions';

export const modifyPdf = () => {
  const { canCreate2307Form } = usePermissions();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const modification = async (pdfData?: TaxCertificateData) => {
    setIsLoading(true);
    try {
      // Fetch the existing PDF (example URL)
      const url = '/pdf/form_2307.pdf';
      const existingPdfBytes = await fetch(url).then((res) =>
        res.arrayBuffer()
      );

      // Load it into pdf-lib
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { width, height } = firstPage.getSize();

      const [from_month, from_day, from_year] =
        pdfData?.periodFrom?.split('/') || [];
      const [to_month, to_day, to_year] = pdfData?.periodTo?.split('/') || [];

      const [
        firstTin_supplier,
        secondTin_supplier,
        thirdTin_supplier,
        fourthTin_supplier
      ] = splitSupplierTin(pdfData?.supplierTin);

      const [
        firstTin_company,
        secondTin_company,
        thirdTin_company,
        fourthTin_company
      ] = splitSupplierTin(pdfData?.companyTin);

      // from_month
      firstPage.drawText(`${from_month?.split('').join(' ') ?? ''}`, {
        x: 155,
        y: height - 118,
        size: 12
      });

      // from_day
      firstPage.drawText(`${from_day?.split('').join(' ') ?? ''}`, {
        x: 182,
        y: height - 118,
        size: 12
      });

      // from_year
      firstPage.drawText(`${from_year?.split('').join('  ') ?? ''}`, {
        x: 208,
        y: height - 118,
        size: 12
      });

      // to_month
      firstPage.drawText(`${to_month?.split('').join(' ') ?? ''}`, {
        x: width - 208,
        y: height - 118,
        size: 12
      });

      // to_day
      firstPage.drawText(`${to_day?.split('').join(' ') ?? ''}`, {
        x: width - 182,
        y: height - 118,
        size: 12
      });

      // to_year
      firstPage.drawText(`${to_year?.split('').join('  ') ?? ''}`, {
        x: width - 156,
        y: height - 118,
        size: 12
      });

      // supplier TIN
      firstPage.drawText(`${firstTin_supplier?.split('').join('  ') ?? ''}`, {
        x: 211,
        y: height - 149,
        size: 12
      });

      firstPage.drawText(`${secondTin_supplier?.split('').join('  ') ?? ''}`, {
        x: 262,
        y: height - 149,
        size: 12
      });

      firstPage.drawText(`${thirdTin_supplier?.split('').join('  ') ?? ''}`, {
        x: 314,
        y: height - 149,
        size: 12
      });

      firstPage.drawText(`${fourthTin_supplier?.split('').join('  ') ?? ''}`, {
        x: 369,
        y: height - 149,
        size: 12
      });

      // supplier Name
      firstPage.drawText(`${pdfData?.supplierCompanyName ? pdfData?.supplierCompanyName : pdfData?.supplierName }`, {
        x: 40,
        y: height - 176,
        size: 10
      });

      firstPage.drawText(`${pdfData?.supplierAddress ?? ''}`, {
        x: 40,
        y: height - 205,
        size: 10
      });

      firstPage.drawText(
        `${pdfData?.supplierZipCode?.split('').join('  ') ?? ''}`,
        {
          x: width - 68,
          y: height - 205,
          size: 12
        }
      );

      // Company Details
      firstPage.drawText(`${firstTin_company?.split('').join('  ') ?? ''}`, {
        x: 211,
        y: height - 265,
        size: 12
      });

      firstPage.drawText(`${secondTin_company?.split('').join('  ') ?? ''}`, {
        x: 264,
        y: height - 265,
        size: 12
      });

      firstPage.drawText(`${thirdTin_company?.split('').join('  ') ?? ''}`, {
        x: 314,
        y: height - 265,
        size: 12
      });

      firstPage.drawText(`${fourthTin_company?.split('').join('  ') ?? ''}`, {
        x: 369,
        y: height - 265,
        size: 12
      });

      firstPage.drawText(`${pdfData?.companyName ?? '-'}`, {
        x: 40,
        y: height - 292,
        size: 10
      });

      firstPage.drawText(`${pdfData?.companyAddress ?? '-'}`, {
        x: 40,
        y: height - 321,
        size: 10
      });

      firstPage.drawText(
        `${pdfData?.companyZipCode?.split('').join('  ') ?? ''}`,
        {
          x: width - 68,
          y: height - 321,
          size: 12
        }
      );

      firstPage.drawText(`Supplier of Goods`, {
        x: 40,
        y: height - 375,
        size: 8
      });

      firstPage.drawText(`WC158`, {
        x: 182,
        y: height - 375,
        size: 8
      });

      // Amounts
      firstPage.drawText(`${pdfData?.firstMonthAmount ?? '-'}`, {
        x: 225,
        y: height - 375,
        size: 8
      });

      firstPage.drawText(`${pdfData?.secondMonthAmount ?? '-'}`, {
        x: 298,
        y: height - 375,
        size: 8
      });

      firstPage.drawText(`${pdfData?.thirdMonthAmount ?? '-'}`, {
        x: 371,
        y: height - 375,
        size: 8
      });

      firstPage.drawText(`${pdfData?.totalAmount ?? '-'}`, {
        x: 444,
        y: height - 375,
        size: 8
      });

      firstPage.drawText(`${pdfData?.taxWithheld ?? '-'}`, {
        x: 517,
        y: height - 375,
        size: 8
      });

      // Total Below
      firstPage.drawText(`${pdfData?.firstMonthAmount ?? '-'}`, {
        x: 225,
        y: height / 2 - 44,
        size: 8
      });

      firstPage.drawText(`${pdfData?.secondMonthAmount ?? '-'}`, {
        x: 298,
        y: height / 2 - 44,
        size: 8
      });

      firstPage.drawText(`${pdfData?.thirdMonthAmount ?? '-'}`, {
        x: 371,
        y: height / 2 - 44,
        size: 8
      });

      firstPage.drawText(`${pdfData?.totalAmount ?? '-'}`, {
        x: 444,
        y: height / 2 - 44,
        size: 8
      });

      firstPage.drawText(`${pdfData?.taxWithheld ?? '-'}`, {
        x: 517,
        y: height / 2 - 44,
        size: 8
      });

      firstPage.drawText('SARA VICTORIA M. SALES', {
        x: 240,
        y: height / 2 - 279,
        size: 11
      });

      // Embed e signature Base64 image if exists
      if (eSign && canCreate2307Form) {
        const imgBytes = decodeBase64Img(eSign);

        if (imgBytes) {
          const signatureImage = await pdfDoc.embedPng(imgBytes);

          const imgWidth = 80;
          const imgHeight =
            (signatureImage.height / signatureImage.width) * imgWidth;

          firstPage.drawImage(signatureImage, {
            x: 270,
            y: height / 2 - 275,
            width: imgWidth,
            height: imgHeight
          });
        }
      }

      const pdfBytes = await pdfDoc.save();

      const blob = new Blob([new Uint8Array(pdfBytes)], {
        type: 'application/pdf'
      });
      const blobUrl = URL.createObjectURL(blob);
      setPdfUrl(blobUrl);
    } catch (error) {
      console.error('Error modifying PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return { modification, pdfUrl, isLoading };
};
